import type {
  TeaserBaseProps,
  TeaserBodyDecoratorsProps,
  TeaserBylineProps,
  TeaserFooterProps,
  TeaserHighlightProps,
  TeaserItalicHeadlineProps,
  TeaserMediaProps,
  TeaserTextProps,
  TeaserThemeProps,
  TeaserVariantProps,
  TeaserVignetteProps,
} from '@bonniernews/dn-design-system-web/assets/types/teaser-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'
import { Body, Content, Media, Title, Vignette } from '@bonniernews/dn-design-system-web/helpers/teaser.tsx'

import { TeaserCard } from '@bonniernews/dn-design-system-web/components/teaser-card/teaser-card.tsx'
import { TeaserFooter } from '@bonniernews/dn-design-system-web/components/teaser-footer/teaser-footer.tsx'

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/teaser-large](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/teaser-large)
 * - Storybook: [TeaserLarge](https://designsystem.dn.se/?path=/docs/section-teaserlarge--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/teaser-large/teaser-large.scss'`
 */

export interface TeaserLargeProps
  extends TeaserBaseProps,
    TeaserThemeProps,
    TeaserHighlightProps,
    TeaserItalicHeadlineProps,
    TeaserVignetteProps,
    TeaserVariantProps,
    TeaserMediaProps,
    TeaserBylineProps,
    TeaserTextProps,
    TeaserBodyDecoratorsProps,
    TeaserFooterProps {
  /** If the headline should be large (only if `isItalicHeadline` is true) */
  isLargeHeadline?: boolean
  variant?: 'default' | 'podcast'
}

export const TeaserLarge = ({
  title,
  targetLink,
  variant = 'default',
  areaType,
  theme,
  text,
  vignette,
  highlight,
  sticker,
  media,
  bylinePicture,
  isItalicHeadline = false,
  isLargeHeadline = false,
  isFlashingDot = false,
  publicationTime,
  duration,
  sectionName,
  attributes = {},
  classNames,
}: TeaserLargeProps) => {
  const componentClassName = 'ds-teaser'

  const italicsClass =
    isItalicHeadline &&
    (isLargeHeadline && areaType !== 'right' ? 'ds-teaser--large-big-italic' : 'ds-teaser--large-italic')

  const classes = formatClassString([
    'ds-teaser--large',
    componentClassName,
    variant && `${componentClassName}--${variant}`,
    !!media && `${componentClassName}--hide-top-border`,
    italicsClass,
    classNames,
  ])

  const teaserFooterVariant = variant === 'podcast' ? 'podcast' : publicationTime ? 'informationText' : 'default'

  return (
    <TeaserCard areaType={areaType} targetLink={targetLink} theme={theme} classNames={classes} attributes={attributes}>
      <Media media={media} />
      <Content bylinePicture={bylinePicture}>
        <Vignette vignette={vignette} variant={variant} />
        <Title title={title} highlight={highlight} />
        {areaType !== 'right' && <Body text={text} sticker={sticker} isFlashingDot={isFlashingDot} />}
        <TeaserFooter
          footerVariant={teaserFooterVariant}
          publicationTime={publicationTime}
          duration={duration}
          sectionName={sectionName}
        />
      </Content>
    </TeaserCard>
  )
}
